import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Container,
  Grid,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import styles from "./FAQ.module.css";
import { Helmet } from "react-helmet";

const FAQ = () => {
  const faqData = [
    {
      question: "What is AI Background Remover?",
      answer:
        "AI Background Remover is a tool that uses artificial intelligence to automatically detect and remove backgrounds from images. It creates transparent backgrounds or allows you to replace them with new ones, making it ideal for product photos, portraits, and creative projects.",
    },
    {
      question: "What types of images can I use with AI Background Remover?",
      answer:
        "You can use a wide range of images, including product photos, portraits, group photos, and even complex images with fine details like hair, fur, or intricate edges. The AI works best with clear, high-quality images.",
    },
    {
      question: "How accurate is the background removal?",
      answer:
        "Our AI achieves over 95% accuracy in background removal, even for complex subjects like hair, fur, and translucent objects. For perfect results, you can use the refine tool to fine-tune edges.",
    },
    {
      question: "Can I replace the removed background with a new one?",
      answer:
        "Yes, after removing the background, you can replace it with a solid color, gradient, or custom image. Our platform also provides templates and presets for quick background replacement.",
    },
    {
      question: "How long does it take to remove a background?",
      answer:
        "Background removal typically takes between 5 to 15 seconds, depending on the image complexity and server load. Batch processing is available for multiple images.",
    },
    {
      question: "What file formats are supported?",
      answer:
        "We support common formats like JPEG, PNG (with transparency), and WEBP. You can download images with or without transparent backgrounds based on your needs.",
    },
    {
      question: "Can I use the edited images commercially?",
      answer:
        "Yes, all images processed with our platform are royalty-free and can be used for personal or commercial purposes, including e-commerce, marketing, and social media.",
    },
    {
      question: "Does the AI work on low-resolution images?",
      answer:
        "Yes, the AI can process low-resolution images, but results are best with high-quality inputs. For optimal results, we recommend using images with clear details.",
    },
    {
      question: "Is my data safe when using AI Background Remover?",
      answer:
        "Absolutely. We prioritize user privacy and data security. Uploaded images are processed securely and automatically deleted after 24 hours.",
    },
    {
      question: "What resolutions are supported for edited images?",
      answer:
        "We support resolutions from standard sizes (e.g., 512x512) up to 4K UHD (3840x2160). Higher resolutions may require premium credits or subscriptions.",
    },
    {
      question: "Can I remove backgrounds from multiple images at once?",
      answer:
        "Yes, our platform supports batch processing, allowing you to upload and remove backgrounds from multiple images simultaneously for faster results.",
    },
  ];

  // Create schema markup for FAQ
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <Container maxWidth="lg" className={styles.container}>
      <Helmet>
        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <Typography variant="h2" className={styles.heading}>
        Frequently Asked Questions
      </Typography>
      <Typography variant="h6" className={styles.subheading}>
        Can't find the answer you're looking for? Reach out to our customer
        support team
      </Typography>

      <Grid item xs={12} md={8}>
        {faqData.map((faq, index) => (
          <Accordion key={index} className={styles.accordion}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon className={styles.expandIcon} />}
              aria-controls={`panel${index}-content`}
              id={`panel${index}-header`}
            >
              <Typography className={styles.question}>
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography className={styles.answer}>{faq.answer}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Grid>
    </Container>
  );
};

export default FAQ;
